import { Lightning } from '@lightningjs/sdk';
import { getTextureByIconData, getTextureByUrl } from './icon.provider';
import { getScaledTextureSize } from './icon.utils';
import { COLOR_LOCKED } from './icon.constants';
import { WHITE } from '../utils/colors';

/**
 * @typedef {Object} IconViewBox
 * @property {number} w width
 * @property {number} h height
 */

/**
 * @typedef {import("./icon.constants").IconType} IconType
 * @typedef {import("@tornado-rdk/icons").IconCollection} IconCollection
 *
 * @typedef {Object} Picto
 * @property {string} name
 * @property {IconType} type
 * @property {?IconCollection} collection
 */

/**
 * @typedef {import("./icon.constants").IconType} IconType
 *
 * @typedef {Object} IconData
 * @property {IconViewBox} viewBox the sizes icon will be fit into
 * @property {(Picto|string)} picto image asset, could be a Picto object or an absolute URL
 * @property {?number} scale allows to render an original SVG icon in greater or lower resolution
 */

/**
 * Displays an SVG icon rasterized into a required view box
 */
export default class Icon extends Lightning.Component {
  _construct() {
    this._textureScale = 1;
    this._textureLoadedHandler = this._onTextureLoaded.bind(this);
  }

  _detach() {
    this.removeAllListeners('txLoaded');
    this._name = undefined;
  }

  get name() {
    return this._name;
  }

  get color() {
    return super.color;
  }

  set color(value) {
    super.color = COLOR_LOCKED.includes(this._name) ? WHITE : value;
  }

  /**
   * @param {IconData} value icon data to render
   */
  set iconData(value) {
    this._textureScale = value.scale || 1;

    if (value.picto && value.picto.name) {
      this._name = value.picto.name;
    }

    this.iconTexture =
      typeof value.picto === 'string'
        ? getTextureByUrl(value.picto, value.viewBox, this._textureScale)
        : getTextureByIconData(value);
  }

  set iconTexture(value) {
    // The provider hands back a cached Texture, so the same icon at the same size is the very object already on
    // this element. Dropping to null and back would take Lightning through a full texture teardown and rebuild —
    // two dimension updates, two render tree invalidations and two events — to arrive at what is already there.
    // A tile row re-applies the same pictos over and over, so this is worth short circuiting.
    //
    // NOTE: a scaled icon still has to make the round trip even for an identical texture. The swap is what makes
    // txLoaded fire again, and that is what re-applies the scaled dimensions in _onTextureLoaded
    if (this.texture === value && this._textureScale === 1) {
      // Callers waiting on txLoaded (e.g. a fade in from alpha 0.0001) still expect it, the round trip used to emit it
      if (this.displayedTexture === value) {
        this.emit('txLoaded', value);
      }

      return;
    }

    this.texture = null;
    this.removeListener('txLoaded', this._textureLoadedHandler);
    if (this._textureScale !== 1) {
      this.on('txLoaded', this._textureLoadedHandler);
    }
    this.texture = value;
  }

  _onTextureLoaded(texture) {
    const sizes = getScaledTextureSize(texture, this._textureScale);
    if (sizes) {
      this.patch(sizes);
    }
  }
}
